import React, { Suspense, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { LanguageProvider } from './context/LanguageContext.jsx';
import Navbar from './components/Navbar/Navbar';
import Footer from './components/Footer/Footer';
import ProtectedRoute from './components/ui/ProtectedRoute';

// Layouts
import EditorLayout   from './pages/Editor/EditorLayout';
import ReporterLayout from './components/reporter/ReporterLayout';

// Public Pages
import Home               from './pages/Home/Home';
import About              from './pages/About/About';
import Login              from './pages/Login/Login';
import Register           from './pages/Register/Register';
import NotFound           from './pages/NotFound/NotFound';
import EventsPage         from './pages/Event/Event.jsx';
import EventDetail        from './pages/Event/EventDetailPage.jsx';
import VolunteerForm      from './pages/Volunteer/VolunteerForm.jsx';
import ContributionForm   from './pages/Contribution/ContributionPage.jsx';
import FeedbackForm       from './pages/Feedback/FeedbackPage.jsx';
import FeedbackDisplayPage from './pages/Feedback/FeedbackDisplayPage.jsx';
import NewsPage           from './pages/News/News.jsx';
import NewsDetailPage     from './pages/News/NewsDetailPage.jsx';
import Downloads          from './pages/Downloads/Downloads.jsx';
import SectorList         from './pages/Sector/SectorList';
import SectorDetails      from './pages/Sector/SectorDetails';
import ExpertDetailPage   from './pages/Sector/ExpertDetailPage';
import ExpertsListingPage from './Togetherness/ExpertListingPage.jsx';
import VolunteersListingPage from './Togetherness/VolunteerListingPage.jsx';

// Editor Pages
import EditorDashboard from './pages/Editor/EditorDashboard.jsx';
import AllArticles     from './pages/Editor/AllArticles.jsx';
import CreateArticle   from './pages/Editor/CreateArticle.jsx';
import ProfileSetting  from './pages/Editor/ProfileSetting.jsx';

// Reporter Pages
import ReporterDashboard from './pages/Reporter/ReporterDashboard';
import ReporterArticles  from './pages/Reporter/ReporterArticles';
import ReporterCreateArticle from './pages/Reporter/CreateArticle';
import EventCoverage     from './pages/Reporter/EventCoverage';
import MediaUploads      from './pages/Reporter/MediaUploads';
import ReporterProfile   from './pages/Reporter/ReporterProfile';

// Admin
import AdminLayout from './components/admin/AdminLayout';
import AdminDashboard from './pages/Admin/AdminDashboardPage';
import AdminSectors from './pages/Admin/SectorsPage';
import AdminEvents from './pages/Admin/EventsPage';
import AdminNews from './pages/Admin/NewsPage';
import AdminDownloads from './pages/Admin/DownloadsAdminPage';
import AdminVolunteers from './pages/Admin/VolunteersPage';
import AdminContributions from './pages/Admin/ContributorsPage';
import AdminFeedback from './pages/Admin/FeedbackPage';
import AdminUsers from './pages/Admin/UsersPage';
import AdminSettings from './pages/Admin/SettingsPage';
import AdminExperts from './pages/Admin/ExpertsPage';


import './index.css';

const PageLoader = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
    <div className="spinner" style={{ width: 36, height: 36, borderColor: 'rgba(45,122,58,0.15)', borderTopColor: 'var(--green)' }} />
  </div>
);

const AppLayout = () => {
  const location = useLocation();
  const isBareRoute = location.pathname.startsWith('/editor')
                   || location.pathname.startsWith('/reporter');
  const isAdminRoute = location.pathname.startsWith('/admin');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {!isBareRoute && !isAdminRoute && <Navbar />}
      <div style={{ flex: 1 }}>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Public */}
            <Route path="/"                  element={<Home />} />
            <Route path="/about"             element={<About />} />
            <Route path="/login"             element={<Login />} />
            <Route path="/register"          element={<Register />} />
            <Route path="/events"            element={<EventsPage />} />
            <Route path="/timeline"          element={<EventsPage />} />
            <Route path="/events/:id"        element={<EventDetail />} />
            <Route path="/volunteer"            element={<VolunteerForm />} />
            <Route path="/your-plan"             element={<ContributionForm />} />
            <Route path="/submit-plan"           element={<ContributionForm />} />
            <Route path="/contributions"        element={<ContributionForm />} />
            <Route path="/feedback"             element={<FeedbackForm />} />
            <Route path="/suggestions-feedback" element={<FeedbackForm />} />
            <Route path="/suggestion-feedback"  element={<FeedbackForm />} />
            <Route path="/feedback/reviews"     element={<FeedbackDisplayPage />} />
            <Route path="/news"              element={<NewsPage />} />
            <Route path="/news/:id"          element={<NewsDetailPage />} />
            <Route path="/sectors"           element={<SectorList />} />
            <Route path="/sector/:slug"      element={<SectorDetails />} />
            <Route path="/experts/:id"       element={<ExpertDetailPage />} />
            <Route path="/downloads"         element={<Downloads />} />
            <Route path="/expertslist" element={<ExpertsListingPage/>}/>
            <Route path="/volunteerslist" element={<VolunteersListingPage/>}/>

            {/* Editor — nested inside EditorLayout */}
            <Route
              path="/editor"
              element={
                <ProtectedRoute roles={['editor']}>
                  <EditorLayout />
                </ProtectedRoute>
              }
            >
              <Route index                     element={<EditorDashboard />} />
              <Route path="dashboard"          element={<EditorDashboard />} />
              <Route path="articles"           element={<AllArticles />} />
              <Route path="create-article"     element={<CreateArticle />} />
              <Route path="profile"            element={<ProfileSetting />} />
              <Route path="feedback"           element={<FeedbackForm />} />
              <Route path="feedback/reviews"   element={<FeedbackDisplayPage />} />
            </Route>

            {/* Reporter — auth+role guard is inside ReporterLayout */}
            <Route
              path="/reporter"
              element={
                <ProtectedRoute roles={['reporter']}>
                  <ReporterLayout />
                </ProtectedRoute>
              }
            >
              <Route index                     element={<ReporterDashboard />} />
              <Route path="dashboard"          element={<ReporterDashboard />} />
              <Route path="articles"           element={<ReporterArticles />} />
              <Route path="create-article"     element={<ReporterCreateArticle />} />
              <Route path="events"             element={<EventCoverage />} />
              <Route path="media"              element={<MediaUploads />} />
              <Route path="profile"            element={<ReporterProfile />} />
            </Route>

            {/* Admin — nested inside AdminLayout */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute roles={['admin']}>
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route index                     element={<AdminDashboard />} />
              <Route path="dashboard"          element={<AdminDashboard />} />
              <Route path="sectors"            element={<AdminSectors />} />
              <Route path="events"             element={<AdminEvents />} />
              <Route path="news"               element={<AdminNews />} />
              <Route path="downloads"          element={<AdminDownloads />} />
              <Route path="volunteers"         element={<AdminVolunteers />} />
              <Route path="contributions"      element={<AdminContributions />} />
              <Route path="feedback"           element={<AdminFeedback />} />
              <Route path="users"              element={<AdminUsers />} />
              <Route path="settings"           element={<AdminSettings />} />
              <Route path="experts"           element={<AdminExperts />} />
              
            </Route>

            {/* 404 */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </div>
      {!isBareRoute && !isAdminRoute && <Footer />}
    </div>
  );
};

// ScrollToTop: ensure each route navigation starts at the top of the page
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    try {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    } catch (e) {
      window.scrollTo(0, 0);
    }
  }, [pathname]);
  return null;
};

const App = () => (
  <LanguageProvider>
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <AppLayout />
      </BrowserRouter>
    </AuthProvider>
  </LanguageProvider>
);

export default App;